import React, {
	createContext,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react';
import { collection, onSnapshot } from 'firebase/firestore';
import { firestore } from '../utils/firebase';
import { useSelector } from 'react-redux';
import { useGetUserDetailsQuery } from '@/features/user/userApi';
import {
	AUTOMATION_BROKER_STATES_SUBCOLLECTION,
	AUTOMATIONS_COLLECTION,
	normalizeAutomationBrokerState,
	normalizeAutomationCatalogDoc,
} from '@/utils/automationCatalog';

const initialState = {
	automations: [],
	getAutomations: (name) => {},
	isLoading: true,
	isUninitialized: true,
	isError: false,
	isSuccess: false,
};

const mapAutomationDoc = (id, doc) => normalizeAutomationCatalogDoc(id, doc);

const AutomationContext = createContext(initialState);

export const useAutomationContext = () => {
	const context = useContext(AutomationContext);
	if (!context) {
		throw new Error('useAutomationContext must be used within an AutomationProvider');
	}
	return context;
};

export const AutomationProvider = ({ children }) => {
	const [isLoading, setIsLoading] = useState(true);

	const isUninitializedRef = useRef(true);

	const [isError, setIsError] = useState(false);
	const isSuccess = useMemo(
		() => !isError && !isUninitializedRef.current && !isLoading,
		[isError, isLoading],
	);

	const account = useSelector((state) => state.msalAccount.account);
	const email = account?.username;
	const firebaseReady = useSelector((state) => state.firebase.firebaseReady);
	const { currentData: userDetails } = useGetUserDetailsQuery(
		{ email },
		{
			skip: !email || !firebaseReady,
		},
	);

	const userContextRef = useRef({ brokerGroupId: null });
	userContextRef.current = {
		brokerGroupId: userDetails?.brokerGroupId,
	};

	const [automations, setAutomations] = useState([]);
	const [brokerStatesByAutomation, setBrokerStatesByAutomation] = useState({});

	// Read enabled from the primary brokerage.
	const getAutomationBrokerState = (doc) => {
		const { brokerGroupId } = userContextRef.current;
		return (
			brokerStatesByAutomation[doc.id]?.[brokerGroupId] ??
			normalizeAutomationBrokerState({ enabled: doc.globallyEnabled })
		);
	};

	useEffect(() => {
		// The Firestore rules need a signed-in user; a listener opened before sign-in is
		// denied and doesn't retry.
		if (!firebaseReady) return;

		if (isUninitializedRef.current) {
			isUninitializedRef.current = false;
		}

		const unsubscribe = onSnapshot(
			collection(firestore, AUTOMATIONS_COLLECTION),
			(snapshot) => {
				try {
					setAutomations(
						snapshot.docs.map((docSnap) =>
							mapAutomationDoc(docSnap.id, docSnap.data()),
						),
					);
				} catch (e) {
					console.error('Error getting automations from Firebase', e);
					setIsError(true);
				} finally {
					setIsLoading(false);
				}
			},
		);

		return () => unsubscribe();
	}, [firebaseReady]);

	const automationIdsKey = useMemo(
		() =>
			automations
				.map((item) => item.id)
				.sort()
				.join(','),
		[automations],
	);

	useEffect(() => {
		const automationIds = automationIdsKey ? automationIdsKey.split(',') : [];
		if (automationIds.length === 0) {
			setBrokerStatesByAutomation({});
			return;
		}

		const unsubscribes = automationIds.map((automationId) =>
			onSnapshot(
				collection(
					firestore,
					AUTOMATIONS_COLLECTION,
					automationId,
					AUTOMATION_BROKER_STATES_SUBCOLLECTION,
				),
				(snapshot) => {
					try {
						const states = Object.fromEntries(
							snapshot.docs.map((docSnap) => [
								docSnap.id,
								normalizeAutomationBrokerState(docSnap.data() ?? {}),
							]),
						);
						setBrokerStatesByAutomation((prev) => ({
							...prev,
							[automationId]: states,
						}));
					} catch (e) {
						console.error('Error getting automation broker states from Firebase', e);
						setIsError(true);
					}
				},
			),
		);

		return () => unsubscribes.forEach((unsubscribe) => unsubscribe());
	}, [automationIdsKey]);

	// Hide if automation active is false. Enabled is primary brokerStates, else globallyEnabled.
	const getAutomations = (name) => {
		const docs = [...(automations ?? []).slice()]
			.filter((doc) => doc.active)
			.map((doc) => ({
				...doc,
				...getAutomationBrokerState(doc),
			}));
		return name ? docs.find((doc) => doc.name === name) : docs;
	};

	return (
		<AutomationContext.Provider
			value={{
				automations,
				getAutomations,
				isLoading,
				isUninitialized: isUninitializedRef.current,
				isError,
				isSuccess,
			}}
		>
			{children}
		</AutomationContext.Provider>
	);
};
